import React, { useState, useRef } from 'react';
import { Check, ArrowLeft, Upload, X, Image, FileText } from 'lucide-react';

/**
 * Article Preview Component
 * Shows the selected text article with image drop zone
 *
 * v1.1: Text article display with drag-and-drop image upload
 */
function ArticlePreview({
  textArticle,
  droppedImage,
  isSubmitting,
  onImageDrop,
  onRemoveImage,
  onApprove,
  onBack,
}) {
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (file.type.startsWith('image/')) {
        onImageDrop(file);
      } else {
        alert('Please drop an image file (JPG, PNG, etc.)');
      }
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.type.startsWith('image/')) {
        onImageDrop(file);
      } else {
        alert('Please select an image file (JPG, PNG, etc.)');
      }
    }
  };

  const handleDropZoneClick = () => {
    fileInputRef.current?.click();
  };

  if (!textArticle) {
    return null;
  }

  const canApprove = droppedImage && !isSubmitting;

  return (
    <div className="card card-custom p-4 position-relative">
      {/* Loading Overlay */}
      {isSubmitting && (
        <div
          className="position-absolute top-0 start-0 w-100 h-100 d-flex flex-column align-items-center justify-content-center"
          style={{
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            zIndex: 10,
            borderRadius: 'inherit',
          }}
        >
          <div className="spinner-border text-primary mb-3" style={{ width: '3rem', height: '3rem' }} role="status">
            <span className="visually-hidden">Publishing...</span>
          </div>
          <p className="text-white mb-1">Publishing article...</p>
          <small className="text-muted">Please wait, do not close this page</small>
        </div>
      )}

      {/* Back button */}
      <button
        onClick={onBack}
        className="btn btn-link text-white p-0 mb-3 d-flex align-items-center"
        disabled={isSubmitting}
      >
        <ArrowLeft size={18} className="me-1" />
        Back to Articles
      </button>

      <div className="row mb-4">
        {/* Image Drop Zone */}
        <div className="col-12 col-md-6 mb-3 mb-md-0">
          <h6 className="text-white mb-2 d-flex align-items-center">
            <Image size={16} className="me-2" />
            Article Image
          </h6>

          {droppedImage ? (
            // Image Preview
            <div className="position-relative">
              <img
                src={droppedImage.preview}
                alt="Article"
                className="w-100 rounded"
                style={{ maxHeight: '300px', objectFit: 'cover' }}
              />
              <button
                onClick={onRemoveImage}
                disabled={isSubmitting}
                className="btn btn-danger btn-sm position-absolute top-0 end-0 m-2"
                title="Remove image"
              >
                <X size={16} />
              </button>
              <div className="mt-2">
                <small className="text-muted">
                  {droppedImage.file.name} ({(droppedImage.file.size / 1024).toFixed(1)} KB)
                </small>
              </div>
            </div>
          ) : (
            // Drop Zone
            <div
              onClick={handleDropZoneClick}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              className={`d-flex flex-column align-items-center justify-content-center rounded p-5 ${
                isDragOver ? 'border-primary bg-primary bg-opacity-10' : ''
              }`}
              style={{
                minHeight: '200px',
                border: `2px dashed ${isDragOver ? '#0d6efd' : '#6c757d'}`,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
              }}
            >
              <Upload size={48} className={`mb-3 ${isDragOver ? 'text-primary' : 'text-muted'}`} />
              <p className="text-white mb-1">
                {isDragOver ? 'Drop image here' : 'Drag & drop an image here'}
              </p>
              <small className="text-muted">or click to browse</small>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                className="d-none"
              />
            </div>
          )}
        </div>

        {/* Article Info */}
        <div className="col-12 col-md-6">
          <div className="mb-3">
            <span className="badge bg-success">
              <FileText size={12} className="me-1" />
              Text Article
            </span>
          </div>

          <h4 className="text-white mb-2">{textArticle.HeadLine}</h4>

          <p className="mb-3" style={{ color: '#a1a1aa' }}>
            {textArticle.BodyText.length > 200
              ? textArticle.BodyText.substring(0, 197) + '...'
              : textArticle.BodyText}
          </p>

          <small className="text-muted">
            Created: {new Date(textArticle.CreationDate).toLocaleDateString()}
          </small>
        </div>
      </div>

      {/* Full Article Body */}
      <div className="mb-4">
        <h6 className="text-white mb-2">Full Article</h6>
        <div
          className="content-box p-3 rounded"
          style={{
            maxHeight: '300px',
            overflowY: 'auto',
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
          }}
        >
          <div className="text-white" style={{ whiteSpace: 'pre-wrap' }}>
            {textArticle.BodyText}
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="d-flex gap-3 justify-content-end">
        <button
          onClick={onApprove}
          disabled={!canApprove}
          className="btn btn-primary-custom d-flex align-items-center"
          title={!droppedImage ? 'Please add an image first' : ''}
        >
          {isSubmitting ? (
            <span className="spinner-border spinner-border-sm me-2" />
          ) : (
            <Check size={18} className="me-2" />
          )}
          {droppedImage ? 'Approve & Publish' : 'Add Image to Publish'}
        </button>
      </div>

      {/* Help text */}
      {!droppedImage && (
        <div className="text-center mt-3">
          <small className="text-muted">
            You must add an image before publishing the article
          </small>
        </div>
      )}
    </div>
  );
}

export default ArticlePreview;
